import React, { useEffect, useRef, useState } from 'react';
import { updateStopNights } from '../features/journeys/api';

/** A stop must have at least this many nights (0 = day trip). */
const MIN_NIGHTS = 0;
/** Delay before persisting a change, reset on every click to coalesce taps. */
const SAVE_DELAY_MS = 2000;

interface NightsStepperProps {
  /** ID of the stop whose duration this controls. */
  stopId: string;
  /** Persisted stop duration in hours (nights are derived as hours / 24). */
  initialHours?: number | null;
  /** When true, the stepper is read-only (e.g. during AI review of an unsaved stop). */
  disabled?: boolean;
  /** Fires with the new nights count on every change, for live summaries (e.g. the
   *  "nights planned" wheel) — before the debounced persist. Optional. */
  onNightsChange?: (nights: number) => void;
  /** Fires once a change is saved. The nights at a stop move the dates of
   *  what comes after it, so the page can read again what depends on them
   *  (pages/JourneyDetailPage.tsx, the night-train facts). Optional. */
  onSaved?: (nights: number) => void;
}

const hoursToNights = (hours?: number | null): number =>
  hours ? Math.max(MIN_NIGHTS, Math.round(hours / 24)) : MIN_NIGHTS;

/**
 * A compact "− N nights +" stepper.
 *
 * Each click updates the displayed value immediately and (re)starts a 2s timer;
 * only when the user stops clicking for 2s do we persist, so a burst of taps
 * results in a single request. A pending change is flushed on unmount so it is
 * never lost when the drawer closes.
 */
const NightsStepper: React.FC<NightsStepperProps> = ({ stopId, initialHours, disabled = false, onNightsChange, onSaved }) => {
  const [nights, setNights] = useState<number>(() => hoursToNights(initialHours));
  // The save runs from a timer, or on unmount: it calls the handler of the latest render.
  const onSavedRef = useRef(onSaved);
  onSavedRef.current = onSaved;

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedRef = useRef<number>(nights); // last value persisted to the backend
  const pendingRef = useRef<number>(nights); // latest value the user wants saved

  const persist = (value: number) => {
    if (value === savedRef.current) return;
    updateStopNights(stopId, value)
      .then(() => {
        savedRef.current = value;
        onSavedRef.current?.(value);
      })
      .catch(() => {
        // Leave savedRef unchanged so the next change retries this value.
      });
  };

  const scheduleSave = (value: number) => {
    pendingRef.current = value;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      persist(value);
    }, SAVE_DELAY_MS);
  };

  const change = (delta: number) => {
    const next = Math.max(MIN_NIGHTS, nights + delta);
    if (next === nights) return;
    setNights(next);
    onNightsChange?.(next);
    scheduleSave(next);
  };

  // Flush any pending change when the component unmounts so edits aren't lost.
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        persist(pendingRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Two instances can edit the same stop (the timeline card and the stop
  // sheet share journey state, which the page patches on every tick). When
  // the stored hours change under an IDLE instance — nothing pending, and
  // the change isn't the echo of its own save — adopt the external value so
  // both steppers always show the same count without remount tricks.
  const externalNights = hoursToNights(initialHours);
  useEffect(() => {
    if (timerRef.current != null) return; // mid-edit: the user's burst wins
    if (externalNights === savedRef.current) return; // our own save echoed
    savedRef.current = externalNights;
    pendingRef.current = externalNights;
    setNights(externalNights);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalNights]);

  // Quiet, borderless stepper per the prototype (.stepper): − / + are plain
  // muted glyphs, the number is bold with the "nights" word in small text right
  // under it. The container's soft pill background + white round buttons only
  // reveal on hover/focus, keeping the card calm at rest.
  const btnClass =
    'w-6 h-6 grid place-items-center rounded-full border-none bg-transparent text-[#b3bbb6] text-base font-semibold leading-none ' +
    'cursor-pointer transition-all active:scale-90 ' +
    'group-hover:bg-white group-hover:text-[#0e5c40] group-hover:shadow-sm group-focus-within:bg-white group-focus-within:text-[#0e5c40] ' +
    'disabled:text-[#d7ddd9] disabled:cursor-not-allowed disabled:group-hover:bg-transparent disabled:group-hover:shadow-none';

  return (
    <div
      className="group inline-flex items-center gap-1 rounded-full border border-transparent p-[3px] transition-colors hover:bg-[#f4f6f3] hover:border-[rgba(20,40,30,0.06)] focus-within:bg-[#f4f6f3] focus-within:border-[rgba(20,40,30,0.06)]"
      aria-label="Nights at this stop"
    >
      <button
        type="button"
        onClick={() => change(-1)}
        disabled={disabled || nights <= MIN_NIGHTS}
        aria-label="Remove a night"
        className={btnClass}
      >
        −
      </button>
      <div className="min-w-[36px] text-center leading-[1.05]">
        <span className="block font-bold text-[14px] text-[#6c7872] transition-colors group-hover:text-[#16241d]">{nights}</span>
        <span className="block text-[9.5px] text-[#b3bbb6] transition-colors group-hover:text-[#6c7872]">{nights === 1 ? 'night' : 'nights'}</span>
      </div>
      <button
        type="button"
        onClick={() => change(1)}
        disabled={disabled}
        aria-label="Add a night"
        className={btnClass}
      >
        +
      </button>
    </div>
  );
};

export default NightsStepper;
